import { Center, Flex, Text } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { SidebarContent } from 'src/UI/app-container/SidebarContent';
import { ClientStep } from './new-steps/ClientStep';
import { AdviceStep } from './new-steps/AdviceStep';
import { SuitabilityHeader } from './edit/components/SuitabilityHeader';
import { TemplateStep } from './new-steps/TemplateStep';
import { FeeDocumentsStep } from './new-steps/FeeDocumentsStep';
import { PillButton } from './edit/components/PillButton';
import { SuitabilityBody } from './edit/components/SuitabilityBody';
import { SuitabilityLayout } from './edit/components/SuitabilityLayout';
import { SearchPill } from './edit/components/SearchPill';
import { SupportDocumentationStep } from './new-steps/SupportDocumentationStep';
import { NewSuitabilityData, StepId, StepProps } from './types';
import { SiestaBarLayout } from 'src/UI/app-container/sidebar/SiestaBarLayout';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { SuitabilitySidebar } from './edit/components/SuitabilitySidebar';
import { ProcessingStep } from './new-steps/ProcessingStep';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { ReportFormatSwitch } from './components/ReportFormatSwitch';

const initialData = (flowId: string): NewSuitabilityData => ({
  flowId,
  client: undefined,
  adviceFiles: [],
  optimiseAdviceReadability: true,
  template: undefined,
  reportFormat: 'REPORT',
  feeDocuments: [],
  supportDocuments: [],
  highlightedElement: undefined,
  isFamily: false,
});

type StepDef = {
  id: StepId;
  title: string;
  disabledButtonlabel: string;
  render: (props: StepProps) => React.ReactNode;
  canContinue?: (data: NewSuitabilityData) => boolean;
  canFilter?: boolean;
};

export default function NewSuitabilityFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = location.state?.fromPath ?? `/clients/all`;

  const { flagEnabledNewSuitabilitySiestaBar } = useFlags();
  const { client: currentClient } = useCurrentClient();

  const flowId = useMemo(() => location.state?.flowId ?? uuid(), [location.state?.flowId]);
  const [data, setData] = useState<NewSuitabilityData>(() => initialData(flowId));
  const [stepIndex, setStepIndex] = useState(0);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setFilter('');
  }, [stepIndex]);

  const steps: StepDef[] = useMemo(
    () => [
      {
        id: 'client',
        title: data.isFamily ? 'Select family' : 'Select client',
        disabledButtonlabel: data.isFamily ? 'Select the family' : 'Select the client',
        canContinue: d => (d.isFamily ? !!d.family?.id : !!d.client?.id),
        canFilter: true,
        render: props => <ClientStep {...props} preselectClientId={currentClient?.id} />,
      },
      {
        id: 'advice',
        title: 'Select advice',
        disabledButtonlabel: 'Select documents',
        canContinue: () => true,
        canFilter: true,
        render: props => <AdviceStep {...props} />,
      },
      {
        id: 'documents',
        title: 'Select documents',
        disabledButtonlabel: 'Select documents',
        canContinue: () => true,
        canFilter: true,
        render: props => <FeeDocumentsStep {...props} />,
      },
      {
        id: 'template',
        title: 'Select template',
        disabledButtonlabel: 'Select template and continue',
        canContinue: d => !!d.template,
        render: props => <TemplateStep {...props} />,
      },
      {
        id: 'support',
        title: 'Select supporting documentation',
        disabledButtonlabel: 'Select documents',
        canContinue: () => true,
        canFilter: true,
        render: props => <SupportDocumentationStep {...props} />,
      },
      {
        id: 'processing',
        title: 'Processing',
        disabledButtonlabel: 'Processing...',
        canContinue: () => true,
        canFilter: false,
        render: props => <ProcessingStep {...props} />,
      },
    ],
    [data.isFamily, currentClient?.id],
  );

  const current = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const canContinue = current.canContinue ? current.canContinue(data) : true;

  const canFilter = current.canFilter ?? false;
  const placeholder = useMemo(() => {
    if (current.id !== 'client') return 'Search';

    return data.isFamily ? 'Search family by name/date' : 'Search client by name/date';
  }, [current.id, data.isFamily]);

  const resetCurrentStepSelection = useCallback(() => {
    setData(prev => {
      switch (current.id) {
        case 'advice':
          return { ...prev, adviceFiles: [] };

        case 'template':
          return { ...prev, template: undefined };

        case 'documents':
          return { ...prev, feeDocuments: [] };

        case 'support':
          return { ...prev, supportDocuments: [] };

        default:
          return prev;
      }
    });
  }, [current.id, setData]);

  const goBack = useCallback(() => {
    if (!isFirst) setStepIndex(i => i - 1);
    resetCurrentStepSelection();
  }, [isFirst, resetCurrentStepSelection]);

  const goNext = useCallback(() => {
    if (!isLast) setStepIndex(i => i + 1);
  }, [isLast]);

  const onClose = useCallback(() => {
    navigate(backHref);
  }, [navigate, backHref]);

  const disabledButtonLabel = current.disabledButtonlabel;
  const stepLabelMap: Partial<Record<StepId, string>> = {
    support: 'Generate report',
  };
  const continueLabel = !canContinue
    ? disabledButtonLabel
    : (stepLabelMap[current.id] ?? 'Continue');

  return (
    <>
      <Flex direction="column" h="100%" pos="relative">
        <SuitabilityLayout
          header={
            !isLast ? (
              <SuitabilityHeader onClose={onClose} onBack={stepIndex > 0 ? goBack : undefined} />
            ) : undefined
          }
          body={
            <SuitabilityBody
              footerAlign="center"
              footer={
                <Flex direction="column" w={360}>
                  {canFilter && (
                    <SearchPill value={filter} onChange={setFilter} placeholder={placeholder} />
                  )}

                  {current.id === 'template' && data.template?.id && (
                    <Flex justify="center">
                      <ReportFormatSwitch
                        value={data.reportFormat}
                        onChange={reportFormat => setData(prev => ({ ...prev, reportFormat }))}
                      />
                    </Flex>
                  )}

                  {current.id !== 'processing' && (
                    <PillButton
                      fullWidth
                      disabled={!canContinue}
                      label={continueLabel}
                      onClick={goNext}
                    />
                  )}
                </Flex>
              }
            >
              {current.render({ data, setData, onNext: goNext, filter })}
            </SuitabilityBody>
          }
        />
      </Flex>
      <SidebarContent>
        {flagEnabledNewSuitabilitySiestaBar ? (
          <SiestaBarLayout>
            <SuitabilitySidebar
              stepIndex={stepIndex}
              steps={steps}
              data={data}
              setData={setData}
              currentStepId={current.id}
            />
          </SiestaBarLayout>
        ) : (
          <Center h="100%">
            <Text size="xl" c="white">
              Step {stepIndex + 1} of {steps.length}: {current.title}
            </Text>
          </Center>
        )}
      </SidebarContent>
    </>
  );
}
